import React, { Component } from 'react';
import { connect } from 'react-redux';

import { ProductLine, RegionDto, UserRegionDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import RegionsSelect from './RegionsSelect';
import ProductLineSelect from '../ProductLineSelect';
import { setSelectedProductLine } from '../../../store/actions/productLineActions';
import { ReduxState } from '../../../store';
import { refreshGeoTraxState } from '../../../services/appService';

/**
 * Filters the Regions by the selected product line, and ensures Region is valid
 * for the selected product line.
 *
 * There are OnChange hooks available to tie into.
 */

export class ProductLineAndRegionManager extends Component<
  ProductLineAndRegionManagerProps,
  ProductLineAndRegionManagerState
> {
  constructor(props: ProductLineAndRegionManagerProps) {
    super(props);
    this.state = {
      regionsWithSelectedProductLine: [],
    };
  }

  static defaultProps: Partial<ProductLineAndRegionManagerProps> = {
    regions: [],
    selectedProductLine: ProductLine.None,
  };

  componentDidMount(): void {
    this.determineAndSetRegionBasedOnProductLine(this.props.selectedProductLine);
    const applicableRegions = this.regionsWithSelectedProductLine();
    this.setState({ regionsWithSelectedProductLine: applicableRegions });
  }

  componentDidUpdate(prevProps: Readonly<ProductLineAndRegionManagerProps>): void {
    const { selectedProductLine, regions } = this.props;
    const selectedProductLineChanged = prevProps.selectedProductLine !== selectedProductLine;
    const regionsChanged = prevProps.regions !== regions;
    if (selectedProductLineChanged || regionsChanged) {
      this.determineAndSetRegionBasedOnProductLine(selectedProductLine);
      const regions = this.regionsWithSelectedProductLine();
      this.setState({ regionsWithSelectedProductLine: regions });
    }
  }

  defaultRegion = () => this.props.regions.find(r => r.url === this.props.defaultRegionUrl);

  isProductLineInCurrentRegion = (pl: ProductLine) =>
    pl && this.props.currentRegion && this.props.currentRegion.productLines?.includes(pl);

  isProductLineInDefaultRegion = (pl: ProductLine) =>
    pl && this.defaultRegion()?.productLines?.includes(pl);

  static ByNameSorter = (r1: RegionDto, r2: RegionDto) => r1.name!.localeCompare(r2?.name!);

  BySelectedProductLineFilter = (r: RegionDto) =>
    r.productLines?.includes(this.props.selectedProductLine);

  regionsWithSelectedProductLine = () =>
    this.props.regions.filter(this.BySelectedProductLineFilter);

  validRegionsSortedByName = () =>
    this.regionsWithSelectedProductLine().sort(ProductLineAndRegionManager.ByNameSorter);

  // covers the edge case where the current region is not in the selected Product Line
  // (like possibly when navigating to Map or Orders from Admin);
  bestRegionForProductLine = (pl: ProductLine) => {
    if (this.isProductLineInCurrentRegion(pl)) {
      return this.props.currentRegion;
    }

    if (this.isProductLineInDefaultRegion(pl)) {
      return this.defaultRegion()!;
    }

    return this.validRegionsSortedByName().at(0)!;
  };

  determineAndSetRegionBasedOnProductLine = (pl: ProductLine): UserRegionDto => {
    const { currentRegion, onRegionChanged } = this.props;
    const bestRegion = this.bestRegionForProductLine(pl);

    if (currentRegion?.url === bestRegion?.url) {
      return currentRegion;
    }
    // change region once bestRegion is findable, race conditions apply
    if (bestRegion) {
      onRegionChanged(bestRegion.url!);
    }
    return bestRegion;
  };

  onProductLineChanged = (productLine: ProductLine) => {
    const { refreshGeoTraxState, onProductLineChanged, userUrl, messageSubscription } = this.props;
    const validRegion = this.determineAndSetRegionBasedOnProductLine(productLine);
    refreshGeoTraxState(validRegion, productLine, userUrl, messageSubscription);
    onProductLineChanged?.(productLine);
  };

  onRegionChanged = (region: RegionDto) => {
    this.props.onRegionChanged(region.url!);
  };

  render() {
    return (
      <div>
        <ProductLineSelect
          disabled={this.props.productLinesDisabled ?? false}
          onSelectionChanged={this.onProductLineChanged}
        />
        <RegionsSelect
          onSelectionChanged={this.onRegionChanged}
          regions={this.state.regionsWithSelectedProductLine}
        />
      </div>
    );
  }
}

export interface ProductLineAndRegionManagerProps {
  defaultRegionUrl: string;
  regions: RegionDto[];
  currentRegion: UserRegionDto;
  messageSubscription: { unsubscribe: () => void }[];
  productLinesDisabled?: boolean;
  selectedProductLine: ProductLine;
  userUrl?: string;
  onProductLineChanged?: (productLine: ProductLine) => void;
  onRegionChanged: (regionUrl: string) => void;
  refreshGeoTraxState: (
    region?: { url?: string; name?: string },
    productLine?: ProductLine,
    userUrl?: string,
    messageSubscriptions?: {
      url?: string;
      unsubscribe: VoidFunc;
    }[],
  ) => void;
}

export interface ProductLineAndRegionManagerState {
  regionsWithSelectedProductLine: RegionDto[];
}

function mapStateToProps(state: ReduxState) {
  return {
    currentRegion: state.currentRegion,
    defaultRegionUrl: state.defaultRegionList[0].url,
    messageSubscription: state.messageSubscription,
    selectedProductLine: state.selectedProductLine,
    userUrl: state.userUrl,
  };
}

export default connect<any, any, any, ReduxState>(mapStateToProps, {
  setSelectedProductLine,
  refreshGeoTraxState,
})(ProductLineAndRegionManager);
